/**
 * 初期データ移行用CSVインポート(kickoff 1-6)。
 * テンプレート: docs/templates/(operators_template.csv / companies_template.csv)
 *
 * オペレーター: 同一氏名で複数行(BB ID違い)が来た場合は 1オペレーターに bb_accounts[] として統合する。
 * 既存オペレーターと同名の行は BB IDの追記のみ行う(時給等は上書きしない)。
 * BB ID・内線番号が空欄の行はシステムで自動生成する(既存スタッフは現在のIDを記入)。
 */
import { sha256Hex, type CsvTable } from '@core/lib/csv';
import { isValidDateStr } from '@core/lib/date';
import { parseAmount } from '@core/lib/money';
import {
  BUSINESS_CODES,
  OPERATOR_STATUS_LABELS,
  PAY_TYPE_CODES,
  PAY_TYPE_LABELS,
  WORK_STYLE_CODES,
  WORK_STYLE_LABELS,
  type BbAccount,
  type BusinessType,
  type Company,
  type Operator,
  type OperatorStatus,
  type PayType,
  type WorkStyle,
  type Yen,
} from '@core/lib/types';
import { appendBbAccounts, createOperator, writeImportLog } from './operatorRepo';
import { createCompany } from './companyRepo';
import { fetchBbNumbering, fillAccountNumbers, normalizePayType, usedBbIds } from './bbNumbering';
import { saveBbCredential } from './bbCredentialRepo';

export type RowError = { lineNo: number; reason: string };

// ---- 共通 -------------------------------------------------------------------

/** ヘッダー名のゆるい一致(「架電内容(給湯器/不用品)」のような注記付きも許容) */
function findColumn(headers: string[], name: string): number {
  const norm = (s: string) => s.replace(/[\s　]/g, '').toLowerCase();
  return headers.findIndex((h) => norm(h).startsWith(norm(name)));
}

function parseBusiness(v: string): BusinessType | null {
  const t = v.trim();
  if (t.startsWith('給湯器')) return 'kyutoki';
  if (t.startsWith('不用品')) return 'fuyohin';
  return parseByCode(BUSINESS_CODES, t);
}

/** MTG で確定した1文字コード(出勤形態 M/A/H/T、報酬区分 H/C、架電内容 A/B)→ 保存値。2026-09-17 */
function parseByCode<K extends string>(codes: Record<K, string>, v: string): K | null {
  const t = v.trim().toUpperCase();
  if (t.length !== 1) return null;
  return (Object.entries(codes) as [K, string][]).find(([, c]) => c === t)?.[0] ?? null;
}

/** 日本語ラベル → コード(前方一致・空白無視) */
function parseByLabel<K extends string>(labels: Record<K, string>, v: string): K | null {
  const t = v.trim().replace(/[\s　]/g, '');
  if (t === '') return null;
  for (const [code, label] of Object.entries(labels) as [K, string][]) {
    if (label === t || label.startsWith(t) || code === t) return code;
  }
  return null;
}

/** 'YYYY/MM/DD' や 'YYYY.MM.DD' も受け付けて 'YYYY-MM-DD' にする */
function parseDate(v: string): string | null | 'invalid' {
  const t = v.trim();
  if (t === '') return null;
  const m = t.match(/^(\d{4})[-/.年](\d{1,2})[-/.月](\d{1,2})日?$/);
  if (!m) return 'invalid';
  const d = `${m[1]}-${m[2]!.padStart(2, '0')}-${m[3]!.padStart(2, '0')}`;
  return isValidDateStr(d) ? d : 'invalid';
}

// ---- オペレーターCSV --------------------------------------------------------

const OPERATOR_COLUMNS = [
  '名前',
  'BB_ID',
  '内線番号',
  '架電会社',
  '架電内容',
  '在籍',
  '出勤形態',
  '報酬区分',
  '時給or単価',
  '交通費往復',
  '入社日',
  '退職日',
  '備考',
  'BBパスワード',
] as const;
/** 空欄でもよい列(無くてもテンプレートとして受け付ける) */
const OPERATOR_OPTIONAL = new Set<string>(['BB_ID', '内線番号', '架電会社', '交通費往復', '入社日', '退職日', '備考', 'BBパスワード']);

/** 取込行のBB ID(任意でBlueBeanパスワードを伴う。パスワードは bb_credentials にのみ保存する) */
export type ImportAccount = BbAccount & { password?: string };

type OperatorRow = {
  lineNo: number;
  name: string;
  account: ImportAccount;
  work_style: WorkStyle;
  pay_type: PayType;
  wage: Yen;
  commute_cost: Yen;
  status: OperatorStatus;
  joined_at: string | null;
  left_at: string | null;
  note: string;
};

export type OperatorImportCreate = {
  name: string;
  /** 報酬(current_wage)はBB IDごと */
  accounts: ImportAccount[];
  work_style: WorkStyle;
  pay_type: PayType;
  commute_cost: Yen;
  status: OperatorStatus;
  joined_at: string | null;
  left_at: string | null;
  note: string;
};

export type OperatorImportPlan = {
  /** 新規作成(同一氏名の行を統合済み) */
  creates: OperatorImportCreate[];
  /** 既存オペレーターへのBB ID追記 */
  merges: { operator: Operator; accounts: ImportAccount[] }[];
  errors: RowError[];
  warnings: string[];
};

export function planOperatorImport(table: CsvTable, existing: Operator[], companies: Company[]): OperatorImportPlan {
  const col = Object.fromEntries(OPERATOR_COLUMNS.map((c) => [c, findColumn(table.headers, c)])) as Record<
    (typeof OPERATOR_COLUMNS)[number],
    number
  >;
  const errors: RowError[] = [];
  const warnings: string[] = [];

  const missing = OPERATOR_COLUMNS.filter((c) => col[c] < 0 && !OPERATOR_OPTIONAL.has(c));
  if (missing.length > 0) {
    return { creates: [], merges: [], errors: [{ lineNo: 1, reason: `列が見つかりません: ${missing.join(', ')}(テンプレートの1行目を確認してください)` }], warnings };
  }

  const rows: OperatorRow[] = [];
  const seenBbIds = new Map<string, number>(); // CSV内の重複検知
  const existingBbIds = usedBbIds(existing);
  const companyByName = new Map(companies.map((c) => [c.name.replace(/[\s　]/g, ''), c.id]));
  const unknownCompanies = new Set<string>();

  table.rows.forEach((cells, i) => {
    const lineNo = i + 2; // ヘッダーが1行目
    const get = (c: (typeof OPERATOR_COLUMNS)[number]) => (col[c] >= 0 ? (cells[col[c]] ?? '').trim() : '');
    const name = get('名前');
    if (!name) return void errors.push({ lineNo, reason: '名前が空です' });

    const bbId = get('BB_ID');
    if (bbId) {
      const dup = seenBbIds.get(bbId.toLowerCase());
      if (dup) return void errors.push({ lineNo, reason: `BB ID「${bbId}」が${dup}行目と重複しています` });
      seenBbIds.set(bbId.toLowerCase(), lineNo);
      if (existingBbIds.has(bbId.toLowerCase())) return void errors.push({ lineNo, reason: `BB ID「${bbId}」は登録済みです` });
    }
    const ext = get('内線番号');

    const business = parseBusiness(get('架電内容'));
    if (!business) return void errors.push({ lineNo, reason: `架電内容「${get('架電内容')}」は A(給湯器)/B(不用品) のいずれかにしてください` });

    const companyText = get('架電会社');
    let companyId: string | null = null;
    if (companyText) {
      companyId = companyByName.get(companyText.replace(/[\s　]/g, '')) ?? null;
      if (!companyId) unknownCompanies.add(companyText);
    }

    const status = get('在籍') === '' ? 'active' : parseByLabel(OPERATOR_STATUS_LABELS, get('在籍'));
    if (!status) return void errors.push({ lineNo, reason: `在籍「${get('在籍')}」は 在籍/退職/休職/HMT/移行 のいずれかにしてください` });
    // 「品川」はこれまでの名前(2026-09-17 に「出勤」へ変更)。古いCSVも読めるように残す
    const workStyleText = get('出勤形態').replace(/[\s　]/g, '') === '品川' ? '出勤' : get('出勤形態');
    const workStyle = parseByCode(WORK_STYLE_CODES, workStyleText) ?? parseByLabel(WORK_STYLE_LABELS, workStyleText);
    if (!workStyle) return void errors.push({ lineNo, reason: `出勤形態「${get('出勤形態')}」は M(管理)/A(出勤)/H(在宅)/T(タイミー) のいずれかにしてください` });
    const payType = parseByCode(PAY_TYPE_CODES, get('報酬区分')) ?? parseByLabel(PAY_TYPE_LABELS, get('報酬区分'));
    if (!payType) return void errors.push({ lineNo, reason: `報酬区分「${get('報酬区分')}」は H(時給)/C(歩合) のいずれかにしてください` });
    const wage = parseAmount(get('時給or単価') || '0');
    if (wage === null) return void errors.push({ lineNo, reason: `時給or単価「${get('時給or単価')}」が数値ではありません` });
    const commute = parseAmount(get('交通費往復') || '0');
    if (commute === null) return void errors.push({ lineNo, reason: `交通費往復「${get('交通費往復')}」が数値ではありません` });
    const joined = parseDate(get('入社日'));
    if (joined === 'invalid') return void errors.push({ lineNo, reason: `入社日「${get('入社日')}」の形式が正しくありません(YYYY/MM/DD)` });
    const left = parseDate(get('退職日'));
    if (left === 'invalid') return void errors.push({ lineNo, reason: `退職日「${get('退職日')}」の形式が正しくありません(YYYY/MM/DD)` });

    const password = get('BBパスワード');
    rows.push({
      lineNo,
      name,
      account: {
        work_style: workStyle,
        pay_type: normalizePayType(workStyle, payType),
        bb_id: bbId,
        ext: ext || null,
        company_id: companyId,
        business,
        active: status === 'active',
        current_wage: wage,
        wage_history: [],
        ...(password ? { password } : {}),
      },
      work_style: workStyle,
      pay_type: payType,
      wage,
      commute_cost: commute,
      status,
      joined_at: joined,
      left_at: left,
      note: get('備考'),
    });
  });

  if (unknownCompanies.size > 0) {
    warnings.push(`会社マスタに無い架電会社は未設定として取り込みます: ${[...unknownCompanies].join('、')}(先に会社を登録すると紐付きます)`);
  }

  // 同一氏名の行を1オペレーターに統合。報酬条件は最初の行を採用する
  const byName = new Map<string, OperatorRow[]>();
  for (const r of rows) {
    const list = byName.get(r.name) ?? [];
    list.push(r);
    byName.set(r.name, list);
  }

  const existingByName = new Map(existing.map((o) => [o.name, o]));
  const plan: OperatorImportPlan = { creates: [], merges: [], errors, warnings };

  for (const [name, list] of byName) {
    const first = list[0];
    if (!first) continue;
    const conflict = list.find((r) => r.commute_cost !== first.commute_cost || r.status !== first.status);
    if (conflict) {
      warnings.push(`${name}: 行によって交通費/在籍が異なります(${conflict.lineNo}行目)。最初の行の値を採用します(出勤形態・報酬区分・報酬はBB IDごとに保持します)`);
    }
    const accounts = list.map((r) => r.account);
    const found = existingByName.get(name);
    if (found) {
      plan.merges.push({ operator: found, accounts });
      warnings.push(`${name}: 登録済みのため、BB IDの追記のみ行います(時給等は上書きしません)`);
    } else {
      plan.creates.push({
        name,
        accounts,
        work_style: first.work_style,
        pay_type: first.pay_type,
        commute_cost: first.commute_cost,
        status: first.status,
        joined_at: first.joined_at,
        left_at: first.left_at,
        note: first.note,
      });
    }
  }
  return plan;
}

export async function runOperatorImport(
  plan: OperatorImportPlan,
  meta: { fileName: string; fileText: string; rowCount: number; existing: Operator[] },
  userId: string,
): Promise<{ imported: number; merged: number }> {
  const settings = await fetchBbNumbering();
  const used = usedBbIds(meta.existing);
  // CSVで指定された番号は先に使用済みにしておく(自動生成が衝突しないように)
  for (const c of plan.creates) for (const a of c.accounts) if (a.bb_id) used.add(a.bb_id.toLowerCase());
  /** BB IDを確定させ、パスワード付きの行は bb_credentials に保存する */
  const finalize = async (accounts: ImportAccount[], opId: string | null): Promise<BbAccount[]> => {
    const filled = fillAccountNumbers(accounts, settings, used);
    for (let i = 0; i < filled.length; i++) {
      const pw = accounts[i]?.password;
      if (pw) await saveBbCredential({ bb_id: filled[i]!.bb_id, op_id: opId, password: pw, note: 'CSVインポート' }, userId);
    }
    // パスワードは bb_credentials にのみ置き、operators には残さない
    return filled.map((a) => {
      const { password: _pw, ...rest } = a as ImportAccount;
      return rest;
    });
  };
  let imported = 0;
  let merged = 0;
  for (const c of plan.creates) {
    const accounts = await finalize(c.accounts, null);
    await createOperator(
      {
        name: c.name,
        bb_accounts: accounts,
        status: c.status,
        commute_cost: c.commute_cost,
        joined_at: c.joined_at,
        left_at: c.left_at,
        extra_info: [],
        note: c.note,
        // 2026-09-17 MTG の項目はCSVの列に無いので既定値(インボイス控除=未設定・PCレンタル=なし・成績表=公開しない)
        invoice_type: null,
        pc_rental: false,
        stats_visible: false,
      },
      userId,
      { source: 'import' },
    );
    imported++;
  }
  for (const m of plan.merges) {
    const filled = await finalize(m.accounts, m.operator.id);
    merged += await appendBbAccounts(m.operator, filled, userId);
  }
  await writeImportLog({
    type: 'operators',
    file_name: meta.fileName,
    file_hash: await sha256Hex(meta.fileText),
    row_count: meta.rowCount,
    imported,
    merged,
    skipped: plan.merges.length,
    error_count: plan.errors.length,
    by: userId,
  });
  return { imported, merged };
}

// ---- 会社CSV ----------------------------------------------------------------

const COMPANY_COLUMNS = ['社名', '会社単価', '商材', '納品タイミング', 'BB業務コード', '電話番号', '備考'] as const;

export type CompanyImportPlan = {
  creates: { name: string; unit_price: Yen; product_type: BusinessType; delivery_timing: string; bb_business_codes: string[]; phone: string; note: string }[];
  errors: RowError[];
  warnings: string[];
};

export function planCompanyImport(table: CsvTable, existing: Company[]): CompanyImportPlan {
  const col = Object.fromEntries(COMPANY_COLUMNS.map((c) => [c, findColumn(table.headers, c)])) as Record<
    (typeof COMPANY_COLUMNS)[number],
    number
  >;
  const errors: RowError[] = [];
  const warnings: string[] = [];

  const missing = COMPANY_COLUMNS.filter((c) => col[c] < 0);
  if (missing.length > 0) {
    return { creates: [], errors: [{ lineNo: 1, reason: `列が見つかりません: ${missing.join(', ')}(テンプレートの1行目を確認してください)` }], warnings };
  }

  const existingNames = new Set(existing.map((c) => c.name));
  const seen = new Set<string>();
  const plan: CompanyImportPlan = { creates: [], errors, warnings };

  table.rows.forEach((cells, i) => {
    const lineNo = i + 2;
    const get = (c: (typeof COMPANY_COLUMNS)[number]) => (cells[col[c]] ?? '').trim();
    const name = get('社名');
    if (!name) return void errors.push({ lineNo, reason: '社名が空です' });
    if (seen.has(name)) return void errors.push({ lineNo, reason: `社名「${name}」がCSV内で重複しています` });
    seen.add(name);
    if (existingNames.has(name)) {
      warnings.push(`${name}: 登録済みのためスキップします(単価変更は会社マスタの「改訂を追加」から)`);
      return;
    }
    const price = parseAmount(get('会社単価') || '0');
    if (price === null) return void errors.push({ lineNo, reason: `会社単価「${get('会社単価')}」が数値ではありません` });
    const product = parseBusiness(get('商材'));
    if (!product) return void errors.push({ lineNo, reason: `商材「${get('商材')}」は 給湯器/不用品 のいずれかにしてください` });

    plan.creates.push({
      name,
      unit_price: price,
      product_type: product,
      delivery_timing: get('納品タイミング'),
      // 区切りは半角/全角セミコロン・カンマの揺れを許容する
      bb_business_codes: get('BB業務コード').split(/[;；,、]/).map((v) => v.trim()).filter((v) => v !== ''),
      phone: get('電話番号'),
      note: get('備考'),
    });
  });
  return plan;
}

export async function runCompanyImport(
  plan: CompanyImportPlan,
  meta: { fileName: string; fileText: string; rowCount: number },
  userId: string,
): Promise<{ imported: number }> {
  let imported = 0;
  for (const c of plan.creates) {
    await createCompany({ ...c, status: 'active' }, userId, { source: 'import' });
    imported++;
  }
  await writeImportLog({
    type: 'companies',
    file_name: meta.fileName,
    file_hash: await sha256Hex(meta.fileText),
    row_count: meta.rowCount,
    imported,
    merged: 0,
    skipped: plan.warnings.length,
    error_count: plan.errors.length,
    by: userId,
  });
  return { imported };
}

// ---- テンプレートのダウンロード(docs/templates/ と同内容) -------------------

export const OPERATOR_TEMPLATE_CSV =
  '名前,BB_ID,内線番号,架電会社,架電内容,在籍,出勤形態,報酬区分,時給or単価,交通費往復,入社日,退職日,備考,BBパスワード\n' +
  'サンプル 太郎,SR2000,2000,サンプル商事,給湯器,在籍,在宅,時給,1200,0,2026/04/01,,既存スタッフは現在のIDを記入,\n' +
  'サンプル 太郎,SR2001,2001,サンプル回収,不用品,在籍,在宅,時給,1200,0,2026/04/01,,不用品用の2つ目のID,\n' +
  'サンプル 花子,,,サンプル商事,給湯器,在籍,出勤,時給,1250,860,2026/05/10,,BB ID・内線が空欄なら自動生成(SN1000〜),\n' +
  'サンプル 次郎,,,サンプル商事,給湯器,在籍,在宅,歩合,3000,0,,,歩合は件数単価(RS4000〜),\n' +
  'サンプル 四郎,,,サンプル商事,給湯器,在籍,タイミー,時給,1100,0,,,タイミーは時給のみ(STM001〜),\n';

export const COMPANY_TEMPLATE_CSV =
  '社名,会社単価,商材,納品タイミング,BB業務コード,電話番号,備考\n' +
  'サンプル商事,15000,給湯器,前日,GYU-A;GYU-B,03-0000-0000,テンプレート行(取込前に削除)\n' +
  'サンプル回収,8000,不用品,土曜日,FUY-A,03-0000-0001,\n';

/** Excelで文字化けしないよう BOM を付けてダウンロードする */
export function downloadCsv(fileName: string, content: string): void {
  const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
